import React from 'react';

import DynamicStateService, { DynamicStateScopes } from '~features/DynamicStateService';
import { usePrerenderUser } from '../../hooks/usePrerenderUser';
import {
  FEATURE_RELEASE_DATES,
  type FeatureReleaseFlag,
  type FeatureReleasesStateUser,
  INITIAL_FEATURE_RELEASES_STATE_USER,
} from './FeatureReleases';

type DynamicStateUserWithFeatureReleases = {
  FeatureReleases?: FeatureReleasesStateUser;
};

function useFeatureReleaseVisibility(featureReleaseFlag: FeatureReleaseFlag): [boolean, () => void] {
  const { signupDate } = usePrerenderUser();
  const [currentDynamicState, updateDynamicState] =
    DynamicStateService.useDynamicStateService<DynamicStateUserWithFeatureReleases, Record<string, never>>();

  const featureReleases = currentDynamicState?.user?.FeatureReleases;
  const hasSeenFeatureRelease =
    featureReleases?.[featureReleaseFlag] ?? INITIAL_FEATURE_RELEASES_STATE_USER[featureReleaseFlag] ?? false;

  const shouldShowFeatureRelease = React.useMemo(
    () => signupDate < FEATURE_RELEASE_DATES[featureReleaseFlag] && !hasSeenFeatureRelease,
    [featureReleaseFlag, hasSeenFeatureRelease, signupDate]
  );

  const markFeatureReleaseAsSeen = React.useCallback(() => {
    updateDynamicState({
      scope: DynamicStateScopes.User,
      state: {
        FeatureReleases: {
          ...featureReleases,
          [featureReleaseFlag]: true,
        },
      },
    });
  }, [featureReleaseFlag, featureReleases, updateDynamicState]);

  return [shouldShowFeatureRelease, markFeatureReleaseAsSeen];
}

export default useFeatureReleaseVisibility;
